import type { ReactElement } from 'react';

import { TFunction } from '@mint-lib/i18n';
import {
  CheckmarkOutline,
  CloseOutline,
  DocumentSignature,
  Launch,
  Wikis,
} from '@mint-lib/icons';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';

import { getTooltipMessage } from '../../constants/myCampaigns.js';
import { Co2Order, Co2OrderStatus } from '../../types/co2notifications.js';

type RenderButtonsStatusOptions = {
  status: Co2OrderStatus;
  item: Co2Order;
  onReject: () => void;
  onAccept: () => void;
  onWebsiteButtonClick: () => void;
  readOnlyUser?: boolean;
  co2ReadOnly: boolean;
  isCurrencyConverted?: boolean;
  isTopButtons?: boolean;
  isCertificateAcknowledged: boolean;
  isAcknowledgedCertificateFeatureEnabled: boolean;
};

export const SERVER_DATE_FORMAT = 'yyyy-MM-dd' as const;
export const CLIENT_DATE_FORMAT = 'dd/MM/yyyy' as const;

export const useMapStatusToDescription = (
  t: TFunction,
): Record<Co2OrderStatus, string> => {
  return {
    [Co2OrderStatus.Booked]: t(
      "Your Buying Plan's carbon emissions were offset.",
    ),
    [Co2OrderStatus.Rejected]: t(
      "Your Buying Plan's carbon emissions were not offset.",
    ),
    [Co2OrderStatus.Draft]: t(
      'Your buying plan has come to an end, and you can now see the finalized cost to offset your carbon emissions. Confirm to offset your carbon emissions.',
    ),
    [Co2OrderStatus.Invoiced]: '',
  };
};
export const useMapStatusToTextMessage = (
  t: TFunction,
): Record<Co2OrderStatus, string> => ({
  [Co2OrderStatus.Booked]: t(
    'Service provided in collaboration with ClimatePartner',
  ),
  [Co2OrderStatus.Draft]: t(
    'Service provided in collaboration with ClimatePartner',
  ),
  [Co2OrderStatus.Invoiced]: '',
  [Co2OrderStatus.Rejected]: '',
});

export const mapStatusToButtons = (
  t: TFunction,
): Record<
  Co2OrderStatus,
  (options: RenderButtonsStatusOptions) => ReactElement
> => ({
  [Co2OrderStatus.Invoiced]: (options) => {
    if (options.isAcknowledgedCertificateFeatureEnabled) {
      return (
        <Button
          variant="text"
          data-dd-action-name="CO2 notification: get certificate"
        >
          {options.isCertificateAcknowledged ? (
            <>
              <Launch />
              {t('View certificate')}
            </>
          ) : (
            <>
              <DocumentSignature />
              {t('Get certificate')}
            </>
          )}
        </Button>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="text"
            data-dd-action-name="CO2 notification: get certificate"
          >
            <Wikis />
            {t('Get certificate from website')}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('Get certificate from website')}</TooltipContent>
      </Tooltip>
    );
  },
  [Co2OrderStatus.Booked]: (options) => {
    if (options.isTopButtons) {
      return (
        <div className="flex items-center gap-1 fill-success-dark text-success-dark">
          <CheckmarkOutline className="size-5" />
          <span className={typographyVariants({ variant: 'bodyShort01' })}>
            {t('Accepted')}
          </span>
        </div>
      );
    }

    if (options.isAcknowledgedCertificateFeatureEnabled) {
      return (
        <Button
          variant="text"
          onClick={options.onWebsiteButtonClick}
          data-dd-action-name="CO2 notification: get certificate"
        >
          {options.isCertificateAcknowledged ? (
            <>
              <Launch />
              {t('View certificate')}
            </>
          ) : (
            <>
              <DocumentSignature />
              {t('Get certificate')}
            </>
          )}
        </Button>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="text"
            onClick={options.onWebsiteButtonClick}
            data-dd-action-name="CO2 notification: get certificate"
          >
            <Wikis />
            {t('Get certificate from website')}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('Get certificate from website')}</TooltipContent>
      </Tooltip>
    );
  },
  [Co2OrderStatus.Draft]: (options) => {
    const tooltipMassages = getTooltipMessage(t);
    const tooltip =
      options.readOnlyUser || options.co2ReadOnly
        ? tooltipMassages.CO2_NO_ACCESS
        : null;
    const isDisabled =
      options.readOnlyUser ||
      !options.isCurrencyConverted ||
      options.co2ReadOnly;

    return (
      <div className="flex gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                onClick={options.onReject}
                data-dd-action-name="CO2 notification: reject offset"
                size="small"
                color="error"
                disabled={isDisabled}
              >
                <CloseOutline />
                {t('REJECT')}
              </Button>
            </span>
          </TooltipTrigger>
          {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                onClick={options.onAccept}
                data-dd-action-name="CO2 notification: accept offset"
                size="small"
                disabled={isDisabled}
              >
                <CheckmarkOutline />
                {t('ACCEPT')}
              </Button>
            </span>
          </TooltipTrigger>
          {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
        </Tooltip>
      </div>
    );
  },
  [Co2OrderStatus.Rejected]: () => (
    <div className="flex items-center gap-1 fill-error-dark text-error-dark">
      <CloseOutline className="size-5" />
      <span className={typographyVariants({ variant: 'bodyShort01' })}>
        {t('Rejected')}
      </span>
    </div>
  ),
});
